// PersonDrawer's reads (finance-real, the user 2026-10-23: "opens a modal with all the invoices sent to the person"): the
// person as the Team tab reads them (GET /agency/team on claims, S8, so Owed now is the row's figure; it lists anyone a
// claim names, on the team or no longer), their team invoices from /finance's Invoices list, every status, newest first,
// and whether they have owed claims on no team invoice yet, for New team invoice. New job (the user 2026-10-24: "add line
// items against a freelancer when they do extra work for the agency") needs their draft: the one they have, or a new
// empty one, made as New team invoice makes it.
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useAgencyTeam } from "../../hooks/useAgencyFinance";
import { useAgencyInvoiceList, useCreateTeamInvoice, useTeamInvoiceCandidates } from "../../hooks/useTeamInvoices";
import type { AgencyInvoiceListResponse, AgencyTeam, AgencyTeamPerson, TeamInvoice, TeamInvoiceCreateInput } from "../../types";

export interface PersonDrawerModel {
  teamQuery: UseQueryResult<AgencyTeam>;
  /** Undefined while the team loads, or when it has no such person. */
  person: AgencyTeamPerson | undefined;
  invoicesQuery: UseQueryResult<AgencyInvoiceListResponse>;
  /** Their team invoices, every status, the newest made first. */
  invoices: TeamInvoice[];
  /** For New team invoice's person picker. */
  people: { id: string; name: string }[];
  /** Owed claims on no team invoice yet: New team invoice has some to tick. */
  hasOpenClaims: boolean;
  /** Their draft team invoice, null on none; undefined until it is read. */
  draftId: string | null | undefined;
  /** Their draft: the one they have (the server answers it), else a new one. */
  makeDraft: UseMutationResult<TeamInvoice, Error, TeamInvoiceCreateInput>;
}

/** The newest made first; on the same day, the one that moved last. */
const newestFirst = (a: TeamInvoice, b: TeamInvoice): number =>
  b.createdOn.localeCompare(a.createdOn) || (b.paidOn ?? b.receivedOn ?? b.createdOn).localeCompare(a.paidOn ?? a.receivedOn ?? a.createdOn);

/** The pickers offer the team; someone no longer on it whose claims are still open is offered too, so New team invoice
 * opens on them (the server makes their draft). */
const withPerson = (people: { id: string; name: string }[], person: AgencyTeamPerson | undefined): { id: string; name: string }[] =>
  !person || people.some((p) => p.id === person.id) ? people : [...people, { id: person.id, name: person.name }];

export function usePersonDrawer(personId: string): PersonDrawerModel {
  const teamQuery = useAgencyTeam();
  const invoicesQuery = useAgencyInvoiceList();
  const candidates = useTeamInvoiceCandidates(personId);
  const invoices = (invoicesQuery.data?.rows ?? [])
    .flatMap((r) => (r.kind === "team" && r.invoice.person.id === personId ? [r.invoice] : []))
    .sort(newestFirst);
  const person = teamQuery.data?.people.find((p) => p.id === personId);
  const makeDraft = useCreateTeamInvoice();
  return {
    teamQuery,
    person,
    invoicesQuery,
    invoices,
    people: withPerson(invoicesQuery.data?.people ?? [], person),
    hasOpenClaims: (candidates.data?.openClaims.length ?? 0) > 0,
    draftId: candidates.data?.draftId,
    makeDraft,
  };
}
